
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2, Syringe, Shield, Zap, CheckCircle, Crown, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

const PainlessRCT = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Painless RCT
  const faqs = [
    {
      question: "What is a root canal treatment?",
      answer: "Root canal treatment (RCT) is a dental procedure that treats infection at the center of a tooth (the root canal system). The treatment involves removing the infected pulp, carefully cleaning and disinfecting the inside of the canal, and then filling and sealing the space to prevent further infection."
    },
    {
      question: "Is root canal treatment painful?",
      answer: "Modern root canal treatments are virtually painless. At Asrani Dental, we use advanced anesthesia techniques and technology to ensure your comfort throughout the procedure. Most patients report that having a root canal is no more uncomfortable than getting a filling. Any discomfort after the procedure is typically mild and can be managed with over-the-counter pain medications."
    },
    {
      question: "How long does a root canal procedure take?",
      answer: "Most root canal treatments can be completed in one or two appointments, depending on the complexity of the case. A simple procedure might take 60-90 minutes, while more complex cases might require a second visit. We prioritize thoroughness over speed to ensure the best outcome."
    },
    {
      question: "What happens after a root canal treatment?",
      answer: "After the root canal is completed, a crown is usually recommended to protect the treated tooth. This is because teeth that have undergone root canal treatment can become more brittle over time. The crown restores the tooth's strength and function while providing a natural appearance."
    },
    {
      question: "What are the signs I might need a root canal?",
      answer: "Common signs include severe toothache, prolonged sensitivity to hot or cold, discoloration of the tooth, swelling and tenderness in nearby gums, and a persistent or recurring pimple on the gums. If you're experiencing any of these symptoms, it's important to see a dentist promptly."
    }
  ];

  // Testimonials for Painless RCT
  const testimonials = [
    {
      name: "Aarav Patel",
      comment: "I was terrified of getting a root canal based on stories I'd heard, but Dr. Asrani made the experience completely painless. The procedure was quick, and I felt absolutely no discomfort during or after. I'm so grateful for their gentle approach and expertise.",
      rating: 5
    },
    {
      name: "Meera Joshi",
      comment: "After suffering from severe tooth pain for days, I finally visited Asrani Dental for an emergency appointment. They diagnosed an infection requiring RCT. I was nervous, but the painless procedure was a complete relief. The team's care and professionalism were outstanding.",
      rating: 5
    },
    {
      name: "Vivek Sharma",
      comment: "I had been putting off a root canal for months due to fear, which only made my tooth pain worse. When I finally went to Asrani Dental, I was amazed at how comfortable the entire process was. Dr. Asrani explained everything clearly, and the treatment was truly painless. I should have come in sooner!",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.location.href = "/dental-appointment-booking";
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Painless Root Canal Treatment in Nagpur | Cost of Painless RCT in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Painless Root Canal Treatment
            </h1>
            <p className="text-lg text-dental-muted">
              Advanced root canal therapy with minimal discomfort, saving your natural teeth while eliminating pain and infection.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/rct_Q.png" 
                alt="Painless RCT at Asrani Dental Clinic" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Modern Root Canal Therapy" 
                subtitle="Painless RCT"
                description="Our painless root canal treatments use advanced techniques and technology to save your natural teeth while ensuring your comfort throughout the procedure."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Advanced Anesthesia</h3>
                    <p className="text-dental-muted">Precise, effective local anesthesia techniques that ensure complete comfort during treatment.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Precision Technology</h3>
                    <p className="text-dental-muted">State-of-the-art rotary instruments and digital imaging for more efficient, thorough treatments.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Tooth Preservation</h3>
                    <p className="text-dental-muted">Save your natural teeth and avoid extraction while eliminating infection and pain.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Expert Care</h3>
                    <p className="text-dental-muted">Treatments performed by specialists with extensive experience in endodontic procedures.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Root Canal Treatments" 
            subtitle="Services We Offer"
            description="We provide comprehensive root canal solutions with a focus on patient comfort and successful outcomes."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <Card className="border-l-4 border-l-dental-highlight transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-2">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <CardTitle className="text-xl">Standard Root Canal Therapy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-dental-muted">
                  Complete treatment of infected or inflamed pulp tissue inside the tooth to eliminate pain and save the natural tooth.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-dental-highlight transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-2">
                  <Zap className="w-6 h-6 text-dental-highlight" />
                </div>
                <CardTitle className="text-xl">Emergency Root Canal</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-dental-muted">
                  Urgent care for severe tooth pain, infection, or abscess requiring immediate endodontic treatment.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-dental-highlight transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-2">
                  <Settings className="w-6 h-6 text-dental-highlight" />
                </div>
                <CardTitle className="text-xl">Retreatment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-dental-muted">
                  Specialized procedure for teeth that have had previous root canal therapy but are experiencing new problems.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-dental-highlight transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-2">
                  <Shield className="w-6 h-6 text-dental-highlight" />
                </div>
                <CardTitle className="text-xl">Apicoectomy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-dental-muted">
                  Surgical procedure that removes the tip of a tooth's root when conventional root canal treatment isn't sufficient.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-dental-highlight transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-2">
                  <CheckCircle2 className="w-6 h-6 text-dental-highlight" />
                </div>
                <CardTitle className="text-xl">Cracked Tooth Treatment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-dental-muted">
                  Specialized endodontic care for teeth with cracks that have affected the pulp, often saving teeth that might otherwise require extraction.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-dental-highlight transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-dental-highlight/10 flex items-center justify-center mb-2">
                  <Crown className="w-6 h-6 text-dental-highlight" />
                </div>
                <CardTitle className="text-xl">Post-Treatment Restoration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-dental-muted">
                  Crown placement and other restorative procedures to protect and strengthen teeth after root canal treatment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their experiences with our painless root canal treatments."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about root canal treatments."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Experiencing Tooth Pain?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Don't suffer in silence. Contact our office today to schedule a consultation and learn how our painless root canal treatments can provide relief while saving your natural teeth.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/orthodontics-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/full-mouth-implants-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Full Mouth & Implants
                </Link>
                <Link to="/services/pediatric-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Pediatric Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Save Your Natural Teeth"
        description="Our painless root canal treatments can eliminate pain and infection while preserving your natural teeth. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default PainlessRCT;
